package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelData
import br.com.vvrefeicoes.portal.domain.repository.PurchasesRepository
import kotlinx.coroutines.CancellationException

class PurchasesPanelController(
    private val repository: PurchasesRepository,
    private val dateTimeProvider: DateTimeProvider,
) {
    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var loading by mutableStateOf(false)
        private set

    var refreshing by mutableStateOf(false)
        private set

    var error by mutableStateOf<String?>(null)
        private set

    var data by mutableStateOf<PurchasesPanelData?>(null)
        private set

    private var initialized = false

    suspend fun initialize() {
        if (initialized) return
        initialized = true
        val latest =
            try {
                repository.latestPeriod()
            } catch (exception: CancellationException) {
                throw exception
            } catch (_: Throwable) {
                null
            }
        if (latest != null) {
            selection =
                selection.copy(
                    mode = PeriodMode.MONTH,
                    month = latest,
                    year = latest.year,
                    start = latest,
                    end = latest,
                )
        }
    }

    suspend fun load(keepData: Boolean = false) {
        val preserveData = keepData && data != null
        if (preserveData) {
            refreshing = true
        } else {
            loading = true
        }
        error = null
        try {
            data = repository.panel(selection.startMonth(), selection.endMonth())
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            if (!preserveData) {
                data = null
            }
            error = exception.message ?: "Nao foi possivel carregar o painel de compras."
        } finally {
            loading = false
            refreshing = false
        }
    }

    fun setMode(mode: PeriodMode) {
        if (selection.mode == mode) return
        selection = selection.copy(mode = mode)
    }

    fun selectMonth(month: MonthPeriod) {
        selection = selection.copy(mode = PeriodMode.MONTH, month = month)
    }

    fun selectYear(year: Int) {
        selection = selection.copy(mode = PeriodMode.YEAR, year = year)
    }

    fun selectCustom(
        start: MonthPeriod,
        end: MonthPeriod,
    ) {
        selection = selection.copy(mode = PeriodMode.CUSTOM, start = start, end = end)
    }

    fun dismissError() {
        error = null
    }
}

private fun PeriodSelection.startMonth(): MonthPeriod =
    when (mode) {
        PeriodMode.MONTH -> month
        PeriodMode.YEAR -> MonthPeriod(year, 1)
        PeriodMode.TOTAL -> month
        PeriodMode.CUSTOM -> start
    }

private fun PeriodSelection.endMonth(): MonthPeriod =
    when (mode) {
        PeriodMode.MONTH -> month
        PeriodMode.YEAR -> MonthPeriod(year, 12)
        PeriodMode.TOTAL -> month
        PeriodMode.CUSTOM -> end
    }
